/**
 * Modern Bold · Collection page (modern template)
 * ─────────────────────────────────────────────────────────────────────────
 * Behaviours:
 *   1. Filter drawer — open / close / focus-trap / Esc
 *   2. Quick-drop chips (sticky bar) — open / close / Esc / outside-click
 *   3. Sort dropdown — listbox pattern, arrow-key nav
 *   4. Quick-add — POST /cart/add.js → open cart drawer (1-variant cards)
 *   5. Price range slider — dual-thumb, live display update
 *   6. Filter form — submit on checkbox change (progressive enhancement);
 *      updates "Show N rugs" count in drawer footer live via URL fetch.
 *
 * Wishlist lives in modern-wishlist.js (<mb-wishlist-button> custom
 * element) and is loaded by this section's template independently.
 *
 * No dependencies. ES2017+. IIFE pattern matches modern-bold.js.
 * Loaded only on collection.modern template — NOT added to theme.liquid.
 */
(function () {
  'use strict';

  // ── Helpers ─────────────────────────────────────────────────────────────

  function debounce(fn, wait) {
    let t;
    return function (...args) {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  /** Trap Tab/Shift-Tab focus inside `container`. Returns a cleanup fn. */
  function trapFocus(container) {
    const focusable = 'a[href],button:not([disabled]),input:not([disabled]),select,textarea,[tabindex]:not([tabindex="-1"])';
    function getEls() {
      return Array.from(container.querySelectorAll(focusable)).filter(
        (el) => !el.closest('[hidden]') && !el.closest('[inert]')
      );
    }
    function handler(e) {
      if (e.key !== 'Tab') return;
      const els = getEls();
      if (!els.length) return;
      const first = els[0];
      const last = els[els.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last.focus(); }
      } else {
        if (document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    }
    container.addEventListener('keydown', handler);
    return () => container.removeEventListener('keydown', handler);
  }

  function lockBody() {
    if (document.body.dataset.mbScrollLocked) return;
    const scrollY = window.scrollY;
    document.body.style.top = `-${scrollY}px`;
    document.body.style.overflow = 'hidden';
    document.body.style.width = '100%';
    document.body.style.position = 'fixed';
    document.body.dataset.mbScrollLocked = scrollY;
  }

  function unlockBody() {
    const scrollY = parseInt(document.body.dataset.mbScrollLocked || '0', 10);
    document.body.style.overflow = '';
    document.body.style.top = '';
    document.body.style.width = '';
    document.body.style.position = '';
    delete document.body.dataset.mbScrollLocked;
    window.scrollTo(0, scrollY);
  }

  // ── Quick-add ────────────────────────────────────────────────────────────

  const SECTIONS_TO_REFRESH = ['cart-drawer', 'cart-icon-bubble'];

  function refreshCartSections() {
    const params = SECTIONS_TO_REFRESH.map((s) => `sections=${encodeURIComponent(s)}`).join('&');
    const url = `${(window.routes && window.routes.cart_url) || '/cart'}?${params}&_=${Date.now()}`;
    return fetch(url, { credentials: 'same-origin' })
      .then((r) => r.json())
      .then((state) => {
        if (!state || !state.sections) return;
        const drawer = document.querySelector('cart-drawer');
        if (drawer && state.sections['cart-drawer']) {
          const parsed = new DOMParser().parseFromString(state.sections['cart-drawer'], 'text/html');
          const fresh = parsed.querySelector('#CartDrawer');
          const current = drawer.querySelector('#CartDrawer');
          if (fresh && current) current.innerHTML = fresh.innerHTML;
          drawer.classList.remove('is-empty');
        }
        const bubble = document.getElementById('cart-icon-bubble');
        if (bubble && state.sections['cart-icon-bubble']) {
          const parsed = new DOMParser().parseFromString(state.sections['cart-icon-bubble'], 'text/html');
          const fresh = parsed.querySelector('#cart-icon-bubble');
          if (fresh) {
            const newCount = fresh.querySelector('span[aria-hidden="true"]');
            const oldCount = bubble.querySelector('span[aria-hidden="true"]');
            if (newCount && oldCount) oldCount.textContent = newCount.textContent;
            const al = fresh.getAttribute('aria-label');
            if (al) bubble.setAttribute('aria-label', al);
          }
        }
      });
  }

  function openCartDrawer() {
    const drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.open === 'function') drawer.open();
  }

  function showCardError(article, message) {
    const el = article.querySelector('[data-mb-error]') || article.querySelector('.mb-colcard__error');
    if (!el) return;
    el.textContent = message;
    el.hidden = false;
    setTimeout(() => { el.hidden = true; el.textContent = ''; }, 4000);
  }

  function handleQuickAdd(btn) {
    if (btn.disabled) return;
    const variantId = btn.getAttribute('data-variant-id');
    if (!variantId) return;

    const article = btn.closest('[data-handle]');
    btn.disabled = true;
    btn.classList.add('is-loading');

    const body = JSON.stringify({
      items: [{ id: parseInt(variantId, 10), quantity: 1 }],
      sections: SECTIONS_TO_REFRESH.join(','),
      sections_url: window.location.pathname,
    });

    fetch(`${(window.routes && window.routes.cart_add_url) || '/cart/add'}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body,
    })
      .then((r) => r.json())
      .then((res) => {
        if (res && res.sections) {
          // Inline refresh from the response (saves a round-trip, same pattern as modern-cart-drawer.js)
          const drawer = document.querySelector('cart-drawer');
          if (drawer && res.sections['cart-drawer']) {
            const parsed = new DOMParser().parseFromString(res.sections['cart-drawer'], 'text/html');
            const fresh = parsed.querySelector('#CartDrawer');
            const current = drawer.querySelector('#CartDrawer');
            if (fresh && current) current.innerHTML = fresh.innerHTML;
            drawer.classList.remove('is-empty');
          }
          openCartDrawer();
        } else if (res && res.status && res.description) {
          // Shopify error object
          if (article) showCardError(article, res.description);
        } else {
          return refreshCartSections().then(openCartDrawer);
        }
      })
      .catch(() => {
        if (article) showCardError(article, 'Could not add to cart. Please try again.');
      })
      .finally(() => {
        btn.disabled = false;
        btn.classList.remove('is-loading');
      });
  }

  function initQuickAdd(root) {
    root.addEventListener('click', function (e) {
      const btn = e.target.closest('[data-mb-quick-add]');
      if (!btn) return;
      e.preventDefault();
      handleQuickAdd(btn);
    });
  }

  // ── Filter drawer ────────────────────────────────────────────────────────

  let filterDrawerCleanup = null;

  function openFilterDrawer(root) {
    const drawer = root.querySelector('[data-mb-filter-drawer]');
    const overlay = root.querySelector('[data-mb-filter-overlay]');
    const trigger = root.querySelector('[data-mb-open-filter-drawer]');
    if (!drawer) return;

    drawer.hidden = false;
    // Force reflow then animate
    requestAnimationFrame(() => {
      drawer.classList.add('is-open');
      if (overlay) overlay.classList.add('is-open');
    });
    document.body.setAttribute('aria-hidden', 'false');
    lockBody();
    if (trigger) trigger.setAttribute('aria-expanded', 'true');

    // A11y: mark the rest of the page as `inert` so:
    //  1) assistive-tech can't navigate to footer/header/main while
    //     the dialog is modal (matches the visual modality)
    //  2) axe-core stops scanning content under the overlay (avoids
    //     the false-positive color-contrast hits caused by the dark
    //     overlay shading the footer text below)
    //  3) keyboard tab is naturally trapped inside the drawer
    setSiblingsInert(drawer, true);

    // Focus first focusable element inside drawer
    const firstFocusable = drawer.querySelector('button,input,a,[tabindex]:not([tabindex="-1"])');
    if (firstFocusable) requestAnimationFrame(() => firstFocusable.focus());

    filterDrawerCleanup = trapFocus(drawer);
  }

  // Walk up from `el` to <body>, marking every direct sibling at each
  // level as `inert` (or removing it). This fully isolates the modal.
  function setSiblingsInert(el, on) {
    let cur = el;
    while (cur && cur.parentElement && cur !== document.body) {
      [...cur.parentElement.children].forEach((sib) => {
        if (sib === cur) return;
        if (on) {
          if (!sib.hasAttribute('data-mb-was-inert')) {
            sib.setAttribute('data-mb-was-inert', sib.hasAttribute('inert') ? '1' : '0');
            sib.setAttribute('inert', '');
          }
        } else if (sib.hasAttribute('data-mb-was-inert')) {
          if (sib.getAttribute('data-mb-was-inert') === '0') sib.removeAttribute('inert');
          sib.removeAttribute('data-mb-was-inert');
        }
      });
      cur = cur.parentElement;
    }
  }

  function closeFilterDrawer(root) {
    const drawer = root.querySelector('[data-mb-filter-drawer]');
    const overlay = root.querySelector('[data-mb-filter-overlay]');
    const trigger = root.querySelector('[data-mb-open-filter-drawer]');
    if (!drawer) return;

    drawer.classList.remove('is-open');
    if (overlay) overlay.classList.remove('is-open');

    if (trigger) trigger.setAttribute('aria-expanded', 'false');
    if (filterDrawerCleanup) { filterDrawerCleanup(); filterDrawerCleanup = null; }
    unlockBody();
    // Restore inert state on the rest of the page
    setSiblingsInert(drawer, false);

    // Wait for CSS transition then hide
    drawer.addEventListener('transitionend', function hide() {
      drawer.hidden = true;
      drawer.removeEventListener('transitionend', hide);
    }, { once: true });

    // Restore focus to trigger
    if (trigger) trigger.focus();
  }

  function initFilterDrawer(root) {
    const openBtn = root.querySelector('[data-mb-open-filter-drawer]');
    const drawer  = root.querySelector('[data-mb-filter-drawer]');
    const overlay = root.querySelector('[data-mb-filter-overlay]');

    if (!openBtn || !drawer) return;

    openBtn.addEventListener('click', () => openFilterDrawer(root));

    // Close button inside drawer
    drawer.querySelectorAll('[data-mb-close-filter-drawer]').forEach((btn) => {
      btn.addEventListener('click', () => closeFilterDrawer(root));
    });

    // Overlay click
    if (overlay) overlay.addEventListener('click', () => closeFilterDrawer(root));

    // Escape key
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && drawer.classList.contains('is-open')) {
        closeFilterDrawer(root);
      }
    });
  }

  // ── Quick-drop chips (sticky bar) ────────────────────────────────────────

  /**
   * Position a fixed panel so its top-left aligns with the bottom of `trigger`.
   * Clamps left so the panel never overflows the right edge of the viewport.
   * Used for both quick-drop panels and the sort panel.
   */
  function positionFixedPanel(panel, trigger, alignRight) {
    const rect = trigger.getBoundingClientRect();
    const panelW = panel.offsetWidth || parseInt(getComputedStyle(panel).minWidth, 10) || 200;
    const GAP = 8;
    const top = rect.bottom + GAP;
    let left;
    if (alignRight) {
      left = rect.right - panelW;
    } else {
      left = rect.left;
    }
    // Clamp so panel doesn't escape viewport on either side
    const vw = window.innerWidth;
    left = Math.min(left, vw - panelW - 16);
    left = Math.max(left, 16);
    panel.style.top  = `${top}px`;
    panel.style.left = `${left}px`;
  }

  function initQuickDrops(root) {
    const drops = root.querySelectorAll('[data-mb-quick-drop]');
    if (!drops.length) return;

    function closeAll() {
      drops.forEach((d) => {
        const panel = d.querySelector('[data-quick-drop-panel], .mb-collection__quick-panel');
        const trigger = d.querySelector('[data-quick-drop-trigger]');
        if (panel) panel.hidden = true;
        if (trigger) trigger.setAttribute('aria-expanded', 'false');
        d.classList.remove('is-open');
      });
    }

    drops.forEach((drop) => {
      const trigger = drop.querySelector('[data-quick-drop-trigger]');
      const panel = drop.querySelector('.mb-collection__quick-panel');
      if (!trigger || !panel) return;

      trigger.addEventListener('click', function (e) {
        e.stopPropagation();
        const isOpen = !panel.hidden;
        closeAll();
        if (!isOpen) {
          panel.hidden = false;
          // Position the fixed panel under the trigger button.
          // Must happen after unhiding so offsetWidth is readable.
          positionFixedPanel(panel, trigger, false);
          trigger.setAttribute('aria-expanded', 'true');
          drop.classList.add('is-open');
        }
      });
    });

    // Outside click closes all
    document.addEventListener('click', function (e) {
      if (!e.target.closest('[data-mb-quick-drop]')) closeAll();
    });

    // Escape closes
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') closeAll();
    });

    // Auto-submit filter form when a chip checkbox changes
    root.addEventListener('change', function (e) {
      const input = e.target.closest('[data-mb-filter-input]');
      if (!input) return;
      const form = root.querySelector('[data-mb-filter-form]');
      if (form) form.submit();
    });
  }

  // ── Sort dropdown ────────────────────────────────────────────────────────

  function initSort(root) {
    const wrap    = root.querySelector('[data-mb-sort-wrap]');
    if (!wrap) return;
    const trigger = wrap.querySelector('[data-mb-sort-trigger]');
    const panel   = wrap.querySelector('[data-mb-sort-option]')?.closest('[role="listbox"]');
    const label   = wrap.querySelector('[data-mb-sort-label]');
    if (!trigger || !panel) return;

    function openSort() {
      panel.hidden = false;
      // Align the fixed panel so its right edge lines up with the trigger's
      // right edge (sort button sits at the far right of the filter bar).
      positionFixedPanel(panel, trigger, true);
      trigger.setAttribute('aria-expanded', 'true');
      const active = panel.querySelector('[aria-selected="true"]');
      if (active) active.focus();
    }

    function closeSort() {
      panel.hidden = true;
      trigger.setAttribute('aria-expanded', 'false');
    }

    trigger.addEventListener('click', function (e) {
      e.stopPropagation();
      panel.hidden ? openSort() : closeSort();
    });

    panel.addEventListener('click', function (e) {
      const option = e.target.closest('[data-mb-sort-option]');
      if (!option) return;
      selectSort(option);
    });

    // Arrow key navigation within listbox
    panel.addEventListener('keydown', function (e) {
      const options = Array.from(panel.querySelectorAll('[data-mb-sort-option]'));
      const current = document.activeElement;
      const idx = options.indexOf(current);
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        const next = options[(idx + 1) % options.length];
        if (next) next.focus();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        const prev = options[(idx - 1 + options.length) % options.length];
        if (prev) prev.focus();
      } else if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        if (current && current.hasAttribute('data-mb-sort-option')) selectSort(current);
      } else if (e.key === 'Escape') {
        closeSort();
        trigger.focus();
      }
    });

    function selectSort(option) {
      const value = option.getAttribute('data-sort-value');
      if (!value) return;
      // Update URL
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', value);
      // Keep existing filters
      window.location.assign(url.toString());
    }

    // Outside click
    document.addEventListener('click', function (e) {
      if (!e.target.closest('[data-mb-sort-wrap]')) closeSort();
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !panel.hidden) { closeSort(); trigger.focus(); }
    });
  }

  // Price range: visible inputs ARE the wire inputs (filter.v.price.gte/lte
  // takes whole dollars). No JS sync required — kept as a no-op stub so
  // existing initRoot() calls remain stable.
  function initPriceRange(/* root */) {}

  // ── Live product count in drawer footer ──────────────────────────────────

  const updateDrawerCount = debounce(function () {
    const form = document.querySelector('[data-mb-drawer-form]');
    const countEl = document.querySelector('[data-mb-drawer-product-count]');
    const activeEl = document.querySelector('[data-mb-drawer-active-count]');
    if (!form || !countEl) return;

    const params = new URLSearchParams(new FormData(form));
    // Strip empty params
    for (const [k, v] of [...params.entries()]) {
      if (v === '') params.delete(k);
    }
    const url = `${form.action || window.location.pathname}?${params.toString()}&_mb_count=1`;

    fetch(url, { credentials: 'same-origin', headers: { Accept: 'text/html' } })
      .then((r) => r.text())
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const countNode = doc.querySelector('[data-mb-collection]');
        if (!countNode) return;
        // Try to extract products_count from a meta tag or data attr we can read
        const grid = doc.querySelector('[data-collection-count]');
        if (grid) countEl.textContent = grid.getAttribute('data-collection-count');

        // Also update the badge count
        if (activeEl) {
          let activeCount = 0;
          params.forEach((v, k) => {
            if (k !== 'sort_by' && v) activeCount++;
          });
          activeEl.textContent = activeCount;
        }
      })
      .catch(() => {});
  }, 600);

  function initDrawerForm(root) {
    const form = root.querySelector('[data-mb-drawer-form]');
    if (!form) return;
    form.addEventListener('change', updateDrawerCount);
  }

  // ── Main init ────────────────────────────────────────────────────────────

  function initCollection(root) {
    if (root.dataset.mbCollectionInit) return;
    root.dataset.mbCollectionInit = '1';

    initQuickAdd(root);
    initFilterDrawer(root);
    initQuickDrops(root);
    initSort(root);
    initPriceRange(root);
    initDrawerForm(root);
    initClosePanelsOnScroll(root);
  }

  // Close any open quick-drop / sort panel on page scroll. Panels use
  // position: fixed and don't follow the trigger when the user scrolls
  // — closing them avoids the "panel orphaned in mid-air" effect, and
  // is the convention every dropdown library follows.
  function initClosePanelsOnScroll(root) {
    let lastY = window.scrollY;
    window.addEventListener('scroll', () => {
      const dy = Math.abs(window.scrollY - lastY);
      lastY = window.scrollY;
      if (dy < 4) return;
      root.querySelectorAll('[data-mb-quick-trigger][aria-expanded="true"], [data-mb-sort-trigger][aria-expanded="true"]').forEach((trigger) => {
        const panelId = trigger.getAttribute('aria-controls');
        const panel = panelId && document.getElementById(panelId);
        if (panel) panel.hidden = true;
        trigger.setAttribute('aria-expanded', 'false');
        trigger.classList.remove('is-open');
      });
    }, { passive: true });
  }

  function initAll() {
    document.querySelectorAll('[data-mb-collection]').forEach(initCollection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Re-init on Shopify theme editor section loads
  document.addEventListener('shopify:section:load', initAll);
})();
